import { useTranslation } from 'react-i18next'

import { CsvAnalysis, CsvColumn } from '../useCsvModal'
import { AnalysisBanner, buildErrorText } from '../utils'
import { Icon } from '@/Atomic/Atoms/Icon'
import { InfoBox } from '@/Atomic/Molecules/InfoBox'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'

interface AnalysisStepProps {
  csvAnalysis: CsvAnalysis
  csvColumns: CsvColumn[]
  columnMappings: Record<number, string>
  entityAttributes: Option[]
  banner: AnalysisBanner
}

const BANNER_TYPE_MAP = {
  error: 'danger',
  warning: 'warning',
  info: 'info',
} as const

export const AnalysisStep: React.FC<AnalysisStepProps> = ({
  csvAnalysis,
  csvColumns,
  columnMappings,
  entityAttributes,
  banner,
}) => {
  const { t } = useTranslation()

  const labelFor = (key: string) =>
    entityAttributes.find((attr) => attr.value === key)?.label ?? key

  const {
    totalRows,
    validCount,
    malformedCount,
    duplicateCount,
    duplicateFirstCount,
    issues,
  } = csvAnalysis

  const importCount = validCount
  const duplicateTotalCount = duplicateCount + duplicateFirstCount

  const downloadFailedRows = () => {
    const mappedCols = csvColumns.filter((col) => columnMappings[col.rawIndex])
    const malformedRows = issues.filter((r) => r.issues.includes('malformed'))
    const headers = [...mappedCols.map((col) => col.header), 'Error'].join(',')
    const rows = malformedRows
      .map((issue) => {
        const cells = mappedCols.map((col) => issue.values[col.rawIndex] ?? '')
        const errorText = buildErrorText(issue.errorDetails, entityAttributes)
        return [...cells, `"${errorText.replace(/"/g, '""')}"`].join(',')
      })
      .join('\n')
    const blob = new Blob([`${headers}\n${rows}`], {
      type: 'text/csv;charset=utf-8;',
    })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'failed_rows.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="csv-analysis-step">
      {banner.kind !== 'none' && (
        <InfoBox type={BANNER_TYPE_MAP[banner.kind]} iconPosition="side">
          <div>
            <div>{t(banner.messageKey)}</div>
            {banner.missingLabels && banner.missingLabels.length > 0 && (
              <div>
                {t('components.csv.analysis.missingFields')}{' '}
                {banner.missingLabels.map(labelFor).join(', ')}
              </div>
            )}
          </div>
        </InfoBox>
      )}
      <div className="csv-analysis-summary-row wizard-modal-label">
        <span>{t('components.csv.analysis.totalRows')}</span>
        <span>{totalRows}</span>
      </div>

      {duplicateTotalCount > 0 && (
        <div className="csv-analysis-card csv-analysis-card--warning">
          <div className="csv-analysis-card-header wizard-modal-label">
            <div className="csv-analysis-card-title">
              <Icon icon="alert-circle-solid" color="warning" size="sm" />
              <span>{t('components.csv.analysis.duplicatedRows')}</span>
            </div>
            <span>{duplicateTotalCount}</span>
          </div>
          <div className="csv-analysis-card-row">
            <div className="csv-analysis-card-row-label wizard-modal-meta">
              <Icon icon="clock" size="sm" />
              <span>{t('components.csv.analysis.willBeImported')}</span>
            </div>
            <span className="csv-analysis-card-row-value csv-analysis-card-row-value--positive">
              +{duplicateFirstCount}
            </span>
          </div>
          <div className="csv-analysis-card-row">
            <div className="csv-analysis-card-row-label wizard-modal-meta">
              <Icon icon="clock" size="sm" />
              <span>{t('components.csv.analysis.willNotBeImported')}</span>
            </div>
            <span className="csv-analysis-card-row-value csv-analysis-card-row-value--negative">
              -{duplicateCount}
            </span>
          </div>
        </div>
      )}

      {malformedCount > 0 && (
        <div className="csv-analysis-card csv-analysis-card--danger">
          <div className="csv-analysis-card-header wizard-modal-label">
            <div className="csv-analysis-card-title">
              <Icon icon="alert-circle-solid" color="danger" size="sm" />
              <span>{t('components.csv.analysis.error')}</span>
            </div>
            <span>{malformedCount}</span>
          </div>
          <div className="csv-analysis-card-row">
            <div className="csv-analysis-card-row-label wizard-modal-meta">
              <Icon icon="clock" size="sm" />
              <span>{t('components.csv.analysis.willNotBeImported')}</span>
            </div>
            <span className="csv-analysis-card-row-value csv-analysis-card-row-value--negative">
              -{malformedCount}
            </span>
          </div>
          <button
            type="button"
            className="csv-analysis-card-download wizard-modal-meta"
            onClick={downloadFailedRows}
          >
            <Icon icon="download-circle" size="sm" />
            <span>{t('components.csv.analysis.downloadFailed')}</span>
          </button>
        </div>
      )}

      <div className="csv-analysis-summary-row csv-analysis-summary-row--total wizard-modal-label">
        <span>{t('components.csv.analysis.totalToImport')}</span>
        <span>{importCount}</span>
      </div>
    </div>
  )
}
